import {
  defaultLoadableState,
  type LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import type React from 'react';
import CustomConfigs from '~/constants/customConfigs';
import { SpinnerType } from '~/contracts/vdp';
import { DeepPartial } from '~/types';
import BrowserCookies from '~/utilities/BrowserCookies';
import { SpinnerView } from './constants';

type DummyFnType = () => void;

type OpenSpinnerArgs = {
  view?: SpinnerView;
  hotspotId?: string;
};

type OpenSpinnerFnType = (args: OpenSpinnerArgs) => void;

export type ContextType = {
  hasError: boolean;
  isInTourMode: boolean;
  isSpinnerDisabled: boolean;
  isSpinnerImmersive: boolean;
  isSpinnerScriptInserted: boolean;
  openHotspot: (id: number, openOnlyHotspotCateg?: boolean) => void;
  openImmersive: () => void;
  openSpinner: OpenSpinnerFnType;
  openSpinnerCondition: (hotspotId?: string) => void;
  openSpinnerGallery: DummyFnType;
  openSpinnerKeyFeatures: (hotspotId?: string) => void;
  openSpinnerView360: DummyFnType;
  setHasError: React.Dispatch<React.SetStateAction<boolean>>;
  setIsInTourMode: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSpinnerImmersive: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSpinnerScriptInserted: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenHotspot: React.Dispatch<React.SetStateAction<DummyFnType>>;
  setOpenImmersive: React.Dispatch<React.SetStateAction<DummyFnType>>;
  setOpenSpinner: React.Dispatch<React.SetStateAction<OpenSpinnerFnType>>;
  setShouldHideSpinner: React.Dispatch<React.SetStateAction<boolean>>;
  setShouldOpenSpinnerArgs: React.Dispatch<
    React.SetStateAction<OpenSpinnerArgs | undefined>
  >;
  setSpinnerType: React.Dispatch<React.SetStateAction<SpinnerType>>;
  shouldHideSpinner: boolean;
  shouldOpenSpinnerArgs?: OpenSpinnerArgs;
  spinnerReady: LoadableState<boolean>;
  spinnerType: SpinnerType;
};

const defaultContext: ContextType = {
  hasError: false,
  isInTourMode: false,
  isSpinnerDisabled: false,
  isSpinnerImmersive: false,
  isSpinnerScriptInserted: false,
  openHotspot() {},
  openImmersive() {},
  openSpinner() {},
  openSpinnerCondition() {},
  openSpinnerGallery() {},
  openSpinnerKeyFeatures() {},
  openSpinnerView360() {},
  setHasError() {},
  setIsInTourMode() {},
  setIsSpinnerImmersive() {},
  setIsSpinnerScriptInserted() {},
  setOpenHotspot() {},
  setOpenImmersive() {},
  setOpenSpinner() {},
  setShouldHideSpinner() {},
  setShouldOpenSpinnerArgs() {},
  setSpinnerType() {},
  shouldHideSpinner: false,
  spinnerReady: defaultLoadableState(false),
  spinnerType: ''
};

const SpinnerContext = createContext(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  isSpinnerDisabled: boolean;
}>;

export function SpinnerContextProvider({ ...props }: OwnProps) {
  // Read the cookie client-side as a fallback so SPA navigation re-mounts
  // initialize correctly without waiting for the RSC prop to arrive on webkit.
  const effectivelyDisabled =
    props.isSpinnerDisabled ||
    Boolean(BrowserCookies.get(CustomConfigs.DisableSpinner));

  const spinnerReady = useLoadableState(effectivelyDisabled, {
    initializeAsLoaded: effectivelyDisabled
  });
  const [shouldHideSpinner, setShouldHideSpinner] = useState(false);
  const [shouldOpenSpinnerArgs, setShouldOpenSpinnerArgs] =
    useState<OpenSpinnerArgs>();
  const [openImmersive, setOpenImmersive] = useState<DummyFnType>(
    () => defaultContext.openImmersive
  );
  const [openHotspot, setOpenHotspot] = useState<DummyFnType>(
    () => defaultContext.openHotspot
  );
  const [openSpinner, setOpenSpinner] = useState<OpenSpinnerFnType>(
    () => defaultContext.openSpinner
  );
  const [isSpinnerScriptInserted, setIsSpinnerScriptInserted] = useState(
    defaultContext.isSpinnerScriptInserted
  );
  const [isSpinnerImmersive, setIsSpinnerImmersive] = useState(
    defaultContext.isSpinnerImmersive
  );
  const [isInTourMode, setIsInTourMode] = useState(defaultContext.isInTourMode);
  const [spinnerType, setSpinnerType] = useState(defaultContext.spinnerType);
  const [hasError, setHasError] = useState(defaultContext.hasError);

  const openSpinnerCondition = useCallback((hotspotId?: string) => {
    setShouldOpenSpinnerArgs({ view: SpinnerView.Condition, hotspotId });
  }, []);

  const openSpinnerGallery = useCallback(() => {
    setShouldOpenSpinnerArgs({ view: SpinnerView.Gallery });
  }, []);

  const openSpinnerKeyFeatures = useCallback((hotspotId?: string) => {
    setShouldOpenSpinnerArgs({ view: SpinnerView.Features, hotspotId });
  }, []);

  const openSpinnerView360 = useCallback(() => {
    setShouldOpenSpinnerArgs({ view: SpinnerView.View360 });
  }, []);

  // Opens the spinner when it's ready and spinner arguments are provided
  useEffect(() => {
    if (spinnerReady.data && shouldOpenSpinnerArgs) {
      openSpinner(shouldOpenSpinnerArgs);
    }
  }, [openSpinner, shouldOpenSpinnerArgs, spinnerReady]);

  // Clears spinner arguments when entering immersive mode or Spinner has errored
  useEffect(() => {
    if (isSpinnerImmersive || Boolean(spinnerReady.error))
      setShouldOpenSpinnerArgs(undefined);
  }, [isSpinnerImmersive, spinnerReady.error]);

  const value = useMemo<ContextType>(
    () => ({
      hasError,
      isInTourMode,
      isSpinnerDisabled: effectivelyDisabled,
      isSpinnerImmersive,
      isSpinnerScriptInserted,
      openHotspot,
      openImmersive,
      openSpinner,
      openSpinnerCondition,
      openSpinnerGallery,
      openSpinnerKeyFeatures,
      openSpinnerView360,
      setHasError,
      setIsInTourMode,
      setIsSpinnerImmersive,
      setIsSpinnerScriptInserted,
      setOpenHotspot,
      setOpenImmersive,
      setOpenSpinner,
      setShouldHideSpinner,
      setShouldOpenSpinnerArgs,
      setSpinnerType,
      shouldHideSpinner,
      shouldOpenSpinnerArgs,
      spinnerReady,
      spinnerType
    }),
    [
      effectivelyDisabled,
      hasError,
      isInTourMode,
      isSpinnerImmersive,
      isSpinnerScriptInserted,
      openHotspot,
      openImmersive,
      openSpinner,
      openSpinnerCondition,
      openSpinnerGallery,
      openSpinnerKeyFeatures,
      openSpinnerView360,
      shouldHideSpinner,
      shouldOpenSpinnerArgs,
      spinnerReady,
      spinnerType
    ]
  );

  return (
    <SpinnerContext.Provider value={value}>
      {props.children}
    </SpinnerContext.Provider>
  );
}

SpinnerContextProvider.displayName = 'SpinnerContextProvider';

export const generateSpinnerContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export default SpinnerContext;
